import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { BookOpen, X, CheckCircle2, Clock } from 'lucide-react';
import { DerivedSeries, useSeriesProgress } from '@/hooks/useSeriesProgress';
import { CoverImage } from '@/components/CoverImage';
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area';
import { Link } from 'react-router-dom';
import { FindNextBookButton } from '@/components/FindNextBookButton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';

interface SeriesTrackerCardProps {
  series: DerivedSeries;
  showDismissButton?: boolean;
  showReactivateButton?: boolean;
}

export function SeriesTrackerCard({
  series,
  showDismissButton = true,
  showReactivateButton = false,
}: SeriesTrackerCardProps) {
  const { dismissSeries, reactivateSeries } = useSeriesProgress();

  const progressPercent = series.totalKnown
    ? Math.round((series.positionsRead.length / series.totalKnown) * 100)
    : 0;

  const isComplete = series.totalKnown
    ? series.positionsRead.length >= series.totalKnown
    : false;

  return (
    <Card className="card-elevated">
      <CardHeader>
        <div className="flex items-start justify-between gap-4">
          <div className="flex-1 min-w-0">
            <CardTitle className="text-xl font-bold mb-1 line-clamp-2">
              {series.seriesNameDisplay}
            </CardTitle>
            <p className="text-sm text-muted-foreground">by {series.authorName}</p>
          </div>
          {showReactivateButton ? (
            <Button
              variant="outline"
              size="sm"
              onClick={() => reactivateSeries({ seriesName: series.seriesName, authorName: series.authorName })}
            >
              Reactivate
            </Button>
          ) : showDismissButton ? (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button variant="ghost" size="icon" className="shrink-0">
                  <X className="h-4 w-4" />
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Dismiss this series?</AlertDialogTitle>
                  <AlertDialogDescription>
                    This will remove it from your active series tracking. You can reactivate it
                    later from the Dismissed tab.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction onClick={() => dismissSeries({ seriesName: series.seriesName, authorName: series.authorName })}>
                    Dismiss
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          ) : null}
        </div>
      </CardHeader>

      <CardContent className="space-y-4">
        {/* Progress Section */}
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Progress</span>
            <div className="flex items-center gap-2">
              {isComplete ? (
                <Badge variant="default" className="gap-1">
                  <CheckCircle2 className="h-3 w-3" />
                  Complete
                </Badge>
              ) : (
                <Badge variant="secondary" className="gap-1">
                  <Clock className="h-3 w-3" />
                  In Progress
                </Badge>
              )}
              <span className="font-medium">
                {series.positionsRead.length} read
                {series.totalKnown ? ` of ${series.totalKnown}` : ''}
                {' '}({series.positionsOwned.length} owned)
              </span>
            </div>
          </div>
          {series.totalKnown && (
            <Progress value={progressPercent} className="h-2" />
          )}
        </div>

        {/* Series Books */}
        {series.books.length > 0 && (
          <div className="space-y-2">
            <h4 className="text-sm font-medium flex items-center gap-2">
              <BookOpen className="h-4 w-4" />
              Your Books in This Series
            </h4>
            <ScrollArea className="w-full whitespace-nowrap">
              <div className="flex gap-3 pb-4">
                {series.books.map((book) => {
                  const isRead = book.shelf === 'read';
                  return (
                    <Link
                      key={book.userBookId}
                      to={`/book/${book.bookId}`}
                      className="relative group shrink-0"
                    >
                      <div className="relative">
                        <CoverImage
                          bookId={book.bookId}
                          coverUrl={book.coverUrl}
                          title={book.title}
                          size="sm"
                          className={!isRead ? 'opacity-50' : ''}
                        />
                        <Badge
                          variant={isRead ? 'default' : 'secondary'}
                          className="absolute -top-2 -right-2 h-6 w-6 rounded-full p-0 flex items-center justify-center text-xs"
                        >
                          {book.seriesPosition}
                        </Badge>
                      </div>
                      {isRead && (
                        <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-md opacity-0 group-hover:opacity-100 transition-opacity">
                          <CheckCircle2 className="h-8 w-8 text-white" />
                        </div>
                      )}
                    </Link>
                  );
                })}
              </div>
              <ScrollBar orientation="horizontal" />
            </ScrollArea>
          </div>
        )}

        {/* Find Next Book Button */}
        {!series.isDismissed && <FindNextBookButton series={series} />}

        {/* Dismiss Reason (if dismissed) */}
        {series.isDismissed && series.dismissReason && (
          <div className="text-sm text-muted-foreground italic">
            Reason: {series.dismissReason}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
